import {
  Rocket,
  LineChart,
  TrendingUp,
  FileSearch,
  Scale,
  ThumbsUp,
  ListChecks,
} from "lucide-react";
import PageHero from "../../components/shared/PageHero";
import CtaBand from "../../components/shared/CtaBand";
import "./IpoAdvisoryPage.css";

const SAMPLE_IPOS = [
  {
    name: "Sample Infra Ltd",
    sector: "Infrastructure",
    open: "Illustrative",
    price: "₹180–190",
    lot: "78",
    gmp: "+₹22*",
  },
  {
    name: "Sample Fintech Pvt",
    sector: "Financial Services",
    open: "Illustrative",
    price: "₹320–340",
    lot: "44",
    gmp: "+₹15*",
  },
  {
    name: "Sample Consumer Co",
    sector: "FMCG",
    open: "Illustrative",
    price: "₹95–100",
    lot: "150",
    gmp: "+₹8*",
  },
];

const ANALYSIS = [
  {
    icon: FileSearch,
    title: "IPO Analysis",
    desc: "Business model, promoter background, use of proceeds, and peer comparison before you apply.",
  },
  {
    icon: TrendingUp,
    title: "GMP Tracking",
    desc: "Grey market premium is an informal signal only — we contextualise GMP against fundamentals, never as a guarantee.",
  },
  {
    icon: LineChart,
    title: "Financial Analysis",
    desc: "Revenue growth, margins, debt, and valuation multiples reviewed against listed peers and issue price.",
  },
  {
    icon: Scale,
    title: "Pros & Cons",
    desc: "Balanced view of strengths and risks so you decide with clarity — not FOMO around listing day.",
  },
  {
    icon: ThumbsUp,
    title: "Investment Recommendation",
    desc: "Suitability-led guidance based on risk appetite, holding horizon, and portfolio concentration.",
  },
  {
    icon: ListChecks,
    title: "Listing Review",
    desc: "Post-listing performance notes and when to hold, average, or exit based on your plan.",
  },
];

export default function IpoAdvisoryPage() {
  return (
    <div className="ipo-page">
      <PageHero
        badge="IPO Advisory"
        title="Informed IPO decisions, not guesswork"
        subtitle="Upcoming issue tracking, financial analysis, GMP context, and listing reviews — with clear disclaimers and suitability focus."
        actions={[
          { label: "Book Free Consultation", to: "/contact", primary: true },
          { label: "Talk to an Expert", to: "/contact" },
        ]}
      />

      <section className="pm-section ipo-upcoming">
        <div className="pm-container">
          <span className="pm-badge">
            <span className="pm-badge-dot" />
            Upcoming IPOs
          </span>
          <h2 className="pm-section-title">Sample IPO tracker</h2>
          <p className="pm-section-sub">
            Illustrative data for layout and education only — not live market
            quotes or recommendations.
          </p>

          <div className="ipo-table-wrap pm-card">
            <table className="ipo-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Sector</th>
                  <th>Open</th>
                  <th>Price Band</th>
                  <th>Lot</th>
                  <th>GMP*</th>
                </tr>
              </thead>
              <tbody>
                {SAMPLE_IPOS.map((ipo) => (
                  <tr key={ipo.name}>
                    <td>
                      <Rocket size={14} className="ipo-table__icon" />
                      {ipo.name}
                    </td>
                    <td>{ipo.sector}</td>
                    <td>{ipo.open}</td>
                    <td>{ipo.price}</td>
                    <td>{ipo.lot}</td>
                    <td>{ipo.gmp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ipo-cards">
            {SAMPLE_IPOS.map((ipo) => (
              <article key={`card-${ipo.name}`} className="pm-card ipo-mobile-card">
                <h3>{ipo.name}</h3>
                <ul>
                  <li>
                    <span>Sector</span>
                    <strong>{ipo.sector}</strong>
                  </li>
                  <li>
                    <span>Price Band</span>
                    <strong>{ipo.price}</strong>
                  </li>
                  <li>
                    <span>Lot Size</span>
                    <strong>{ipo.lot}</strong>
                  </li>
                  <li>
                    <span>GMP*</span>
                    <strong>{ipo.gmp}</strong>
                  </li>
                </ul>
              </article>
            ))}
          </div>

          <p className="ipo-disclaimer">
            *All IPO names, dates, price bands, and GMP figures on this page are
            illustrative samples for demonstration. They do not constitute live
            data, research reports, or investment advice. Please verify with
            official offer documents and SEBI/NSE/BSE disclosures.
          </p>
        </div>
      </section>

      <section className="pm-section ipo-analysis">
        <div className="pm-container">
          <span className="pm-badge">
            <span className="pm-badge-dot" />
            Advisory Framework
          </span>
          <h2 className="pm-section-title">How we evaluate IPOs</h2>
          <div className="ipo-analysis-grid">
            {ANALYSIS.map((a) => {
              const Icon = a.icon;
              return (
                <article key={a.title} className="pm-card ipo-card">
                  <div className="ipo-card__icon">
                    <Icon size={22} />
                  </div>
                  <h3>{a.title}</h3>
                  <p>{a.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBand
        title="Planning to apply in an upcoming IPO?"
        subtitle="Get a suitability-focused review before you bid — fundamentals over hype."
        primary={{ label: "Book Free Consultation", to: "/contact" }}
        secondary={{ label: "Talk to an Expert", to: "/contact" }}
      />
    </div>
  );
}
